'use client';

import {
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { terraProxyApiClient } from '../../clients/testerClient';

export const APPLICATION_SOURCE_CODE_INTEGRATIONS_KEY =
  'application-source-code-integrations';
export const APPLICATION_SOURCE_CODE_VALIDATION_KEY =
  'application-source-code-validation';
export const TENANT_GIT_INTEGRATION_DETAILS_KEY =
  'tenant-git-integration-details';

const GIT_INTEGRATION_KEY = 'git-integration';
const SOURCE_CODE_INFO_KEY = 'source-code-info';

export function useApplicationSourceCodeIntegrations({
  applicationId,
}: {
  applicationId: string | undefined;
}) {
  const integrationsQuery = useQuery({
    queryKey: [APPLICATION_SOURCE_CODE_INTEGRATIONS_KEY, applicationId],
    queryFn: async () => {
      if (!applicationId) {
        throw new Error('Application ID is required');
      }

      const { gitIntegrations } =
        await terraProxyApiClient.listApplicationGitIntegrations(applicationId);

      return gitIntegrations;
    },
    enabled: Boolean(applicationId),
  });
  const integrations = integrationsQuery.data ?? [];
  const validationQuery = useQuery({
    queryKey: [
      APPLICATION_SOURCE_CODE_VALIDATION_KEY,
      applicationId,
      integrations.length,
    ],
    queryFn: async () => {
      if (!applicationId) {
        throw new Error('Application ID is required');
      }

      const { results } =
        await terraProxyApiClient.validateApplicationGitIntegrations(
          applicationId,
        );

      return results;
    },
    enabled: Boolean(applicationId) && integrations.length > 0,
  });
  const detailsQueries = useQueries({
    queries: integrations.map((integration) => ({
      queryKey: [
        TENANT_GIT_INTEGRATION_DETAILS_KEY,
        integration.gitIntegrationId,
      ],
      queryFn: async () => {
        if (!integration.gitIntegrationId) {
          throw new Error('Git integration ID is required');
        }

        const { integration: details } =
          await terraProxyApiClient.getTenantGitIntegrationDetails(
            integration.gitIntegrationId,
          );

        return details;
      },
      enabled: Boolean(integration.gitIntegrationId),
    })),
  });
  const items = integrations.map((integration, index) => ({
    appIntegration: integration,
    details: detailsQueries[index]?.data,
    isDetailsLoading: Boolean(
      detailsQueries[index]?.isLoading || detailsQueries[index]?.isFetching,
    ),
    isValid:
      validationQuery.data?.find(
        ({ integrationId }) => integrationId === integration.id,
      )?.isValid ?? null,
  }));

  return {
    items,
    isLoading: integrationsQuery.isLoading,
    isFetching: integrationsQuery.isFetching,
    isError: integrationsQuery.isError,
  };
}

export function useDisconnectApplicationSourceCode({
  applicationId,
}: {
  applicationId: string | undefined;
}) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (integrationId: string) => {
      if (!applicationId) {
        throw new Error('Application ID is required');
      }

      return terraProxyApiClient.disconnectApplicationGitIntegration({
        applicationId,
        integrationId,
      });
    },
    onSuccess: async () => {
      await Promise.all(
        [
          APPLICATION_SOURCE_CODE_INTEGRATIONS_KEY,
          APPLICATION_SOURCE_CODE_VALIDATION_KEY,
          GIT_INTEGRATION_KEY,
          SOURCE_CODE_INFO_KEY,
        ].map((key) =>
          queryClient.invalidateQueries({ queryKey: [key, applicationId] }),
        ),
      );
    },
  });
}
